import { oc } from "@orpc/contract";
import { z } from "zod";
import { ArInvoiceSalespersonSchema } from "./ar-invoices.contract";

/**
 * How a customer sends remittance advice, set by AR on the customer. `none`
 * means they pay without sending one; null (on the customer) means not set.
 */
export const CUSTOMER_REMITTANCE_TYPES = [
  "email",
  "portal",
  "edi",
  "mail",
  "none",
] as const;
export const CustomerRemittanceTypeSchema = z.enum(CUSTOMER_REMITTANCE_TYPES);

export type CustomerRemittanceType = z.infer<
  typeof CustomerRemittanceTypeSchema
>;

/**
 * AR Customer Profile RPC Contract
 *
 * Provides a single endpoint for fetching all deterministic customer data
 * needed by an AR clerk: aging buckets, open/overdue balances, credit
 * utilization, recent payments, payment terms, contact info, behavioral
 * metrics (avg days to pay, on-time rate, trend), and an activity timeline.
 *
 * All data is computed server-side via SQL aggregates. The aging basis
 * (invoice date, due date, or ship date) is a frontend-configurable parameter.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

/**
 * Input schema for getCustomerProfile — requires a specific customer UUID
 * and an optional aging basis (defaults to invoice date).
 */
export const GetArCustomerProfileInputSchema = z.object({
  customerId: z.string().uuid(),
  agingBasis: z
    .enum(["invoiceDate", "dueDate", "shipDate"])
    .default("shipDate"),
});

/**
 * A single recent payment entry (DATA-04)
 */
export const ArRecentPaymentSchema = z.object({
  id: z.string().uuid(),
  paymentDate: z.string(), // ISO date string
  amount: DecimalStringSchema,
  referenceNumber: z.string().nullable(),
  paymentMethod: z.string().nullable(),
});

/**
 * A single activity timeline entry (ACTV-01)
 */
export const ArActivityTimelineItemSchema = z.object({
  id: z.string(),
  type: z.enum([
    "invoice_created",
    "invoice_sent",
    "statement_sent",
    "payment_received",
    "email_inbound",
    "email_outbound",
    "credit_limit_change",
    "customer_note_added",
    "collection_status_changed",
    "remittance_type_changed",
  ]),
  timestamp: z.string(), // ISO datetime string
  description: z.string(), // human-readable description
  amount: DecimalStringSchema.nullable(), // relevant for invoices/payments
  status: z.string().nullable(), // invoice status, payment status, etc.
  referenceId: z.string().nullable(), // ID to link to detail view
  referenceType: z.string().nullable(), // entity type: "invoice", "payment", "email_thread"
  actorUserName: z.string().nullable().optional(), // name of the user who created the activity
});

/**
 * A single inbound email summary for the customer inbox card.
 */
export const ArCustomerInboundEmailSchema = z.object({
  id: z.string().uuid(),
  subject: z.string().nullable(),
  from: z.string().nullable(),
  receivedAt: z.string(), // ISO datetime
});

/**
 * A single outbound email summary for the customer sent emails section.
 */
export const ArCustomerOutboundEmailSchema = z.object({
  id: z.string().uuid(),
  subject: z.string().nullable(),
  to: z.string().nullable(),
  sentAt: z.string(), // ISO datetime
});

/**
 * Full customer profile response schema covering all 10 requirements
 * (DATA-01 through ACTV-01).
 */
export const ArCustomerProfileResponseSchema = z.object({
  /**
   * Aging buckets and balances (DATA-01 + DATA-02)
   */
  aging: z.object({
    /** Balance not yet due (based on agingBasis parameter) */
    current: DecimalStringSchema,
    /** 1-30 days overdue */
    days1_30: DecimalStringSchema,
    /** 31-60 days overdue */
    days31_60: DecimalStringSchema,
    /** 61-90 days overdue */
    days61_90: DecimalStringSchema,
    /** 90+ days overdue */
    days90plus: DecimalStringSchema,
    /** Sum of all unpaid invoices (all buckets) */
    totalOpen: DecimalStringSchema,
    /** Sum of overdue buckets only (days1_30 + days31_60 + days61_90 + days90plus) */
    totalOverdue: DecimalStringSchema,
    /** Count of open (unpaid) invoices */
    invoiceCount: z.number().int().nonnegative(),
  }),

  /**
   * Credit limit and utilization (DATA-03)
   */
  credit: z.object({
    /** Customer credit limit; null if no limit is set */
    creditLimit: DecimalStringSchema.nullable(),
    /** Current open balance used against the limit */
    utilizationAmount: DecimalStringSchema,
    /** Utilization as a percentage (0-100); null when creditLimit is null or 0 */
    utilizationPct: z.number().nullable(),
    /** false when creditLimit is null */
    hasLimit: z.boolean(),
  }),

  /**
   * Incoming payments — this customer's remittances that have been matched to
   * a payment but not yet confirmed against a bank deposit. Mirrors the AR
   * summary "Incoming Payments" metric, scoped to the customer.
   */
  incomingPayments: z.object({
    /** Sum of stated remittance amounts not yet bank-cleared */
    total: DecimalStringSchema,
    /** Count of remittances contributing to the total */
    count: z.number().int().nonnegative(),
  }),

  /**
   * Last 5 payments (DATA-04)
   */
  recentPayments: z.array(ArRecentPaymentSchema),

  /**
   * Customer payment terms (DATA-05)
   */
  terms: z.object({
    /** Payment terms string from ar_customers (e.g. "Net 30") */
    description: z.string().nullable(),
    /** Payment term days from invoice if available */
    termDays: z.number().int().nullable(),
  }),

  /**
   * Customer contact information (DATA-06)
   */
  contact: z.object({
    contactPerson: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
  }),

  /**
   * Behavioral payment metrics (BEHV-01, BEHV-02, BEHV-03)
   */
  behavioral: z.object({
    /** Avg days from invoice date to fully-paid date; null if no paid invoices in 12-month window */
    avgDaysToPay: z.number().nullable(),
    /** Number of invoices used for the avgDaysToPay calculation ("Based on N invoices" qualifier) */
    invoiceCountUsed: z.number().int(),
    /** On-time payment rate as percentage 0-100; null if no data */
    onTimePaymentRate: z.number().nullable(),
    /** Payment trend direction over trailing 6 months */
    trend: z.enum(["improving", "stable", "declining"]),
    /** Total invoices used for trend calculation (both windows combined) */
    trendInvoiceCount: z.number().int(),
    /** true if customer has no invoice history at all */
    isNewCustomer: z.boolean(),
  }),

  /**
   * Unified activity timeline — last ~10 events across all types (ACTV-01)
   */
  activityTimeline: z.array(ArActivityTimelineItemSchema),

  /**
   * Inbound emails from this customer (matched by email domain)
   */
  inboundEmails: z.array(ArCustomerInboundEmailSchema),

  /**
   * Outbound emails sent to this customer (statements, invoices, etc.)
   */
  outboundEmails: z.array(ArCustomerOutboundEmailSchema),

  /**
   * The customer's primary salesperson. Resolved from
   * `Customer.primarySalesPersonContactId` (an internal Contact row, sourced
   * from Famous at ingest). Null when the customer has no rep configured;
   * `email` may be null when Famous carries a name but no comm record.
   */
  salesperson: ArInvoiceSalespersonSchema.nullable(),

  /**
   * Per-customer communication opt-outs. When a flag is true, the matching
   * automated scan skips this customer (payment reminders / invoice emails).
   * Set via `arCustomerComms.setCommsExclusions`.
   */
  commsPreferences: z.object({
    excludeFromPaymentReminders: z.boolean(),
    excludeFromInvoiceEmails: z.boolean(),
  }),

  /**
   * How the customer sends remittance advice; null when AR hasn't set it.
   * Set via `arCustomerComms.setRemittanceType`.
   */
  remittanceType: CustomerRemittanceTypeSchema.nullable(),
});

/**
 * Input for the customer's receipt payment-method mix (check vs EFT).
 * `windowMonths` is the trailing window, default 12.
 */
export const GetArCustomerPaymentMethodMixInputSchema = z.object({
  customerId: z.string().uuid(),
  windowMonths: z.number().int().min(1).max(60).default(12),
});

/** Count + total of the customer's receipts of one payment type. */
export const ArPaymentMethodBucketSchema = z.object({
  count: z.number().int(),
  amount: DecimalStringSchema,
});

/**
 * How a customer has paid over a trailing window, from their Famous cash
 * receipts (BANK_DEPOSIT_LINE.DEPOSITTYPE). Internal adjustments and
 * non-positive receipts are excluded. `unclassified` counts receipts in the
 * window with no/unknown deposit type (e.g. not yet backfilled).
 */
export const ArCustomerPaymentMethodMixSchema = z.object({
  windowMonths: z.number().int(),
  windowStart: z.string(), // YYYY-MM-DD
  check: ArPaymentMethodBucketSchema,
  eft: ArPaymentMethodBucketSchema,
  cash: ArPaymentMethodBucketSchema,
  unclassified: ArPaymentMethodBucketSchema,
  /** Checks as a % of classified receipts by count; null with none. */
  checkSharePct: z.number().nullable(),
  lastCheckDate: z.string().nullable(), // YYYY-MM-DD
  lastEftDate: z.string().nullable(), // YYYY-MM-DD
  /** True when checks are still a meaningful share — see the service's thresholds. */
  stillPaysByCheck: z.boolean(),
  /** Thresholds behind `stillPaysByCheck`, echoed for the UI copy. */
  thresholds: z.object({
    recentCheckDays: z.number().int(),
    checkSharePct: z.number(),
  }),
});

/**
 * AR Customer Profile Contracts
 */
export const arCustomerProfileContracts = {
  get: oc
    .input(GetArCustomerProfileInputSchema)
    .output(ArCustomerProfileResponseSchema)
    .route({
      method: "GET",
      summary: "Get customer AR profile",
      description:
        "Returns a customer's receivables overview: aging balances, credit, incoming and recent payments, terms, contacts, payment behavior, activity timeline, emails, salesperson, and communication opt-outs.",
    }),
  getPaymentMethodMix: oc
    .input(GetArCustomerPaymentMethodMixInputSchema)
    .output(ArCustomerPaymentMethodMixSchema)
    .route({
      method: "GET",
      summary: "Get customer payment method mix",
      description:
        "Returns how a customer paid over a trailing window (default 12 months): check, EFT, and cash receipt counts and totals, and whether they still pay by check.",
    }),
} as const;

// Type exports
export type GetArCustomerProfileInput = z.infer<
  typeof GetArCustomerProfileInputSchema
>;
export type ArCustomerProfileResponse = z.infer<
  typeof ArCustomerProfileResponseSchema
>;
export type ArActivityTimelineItem = z.infer<
  typeof ArActivityTimelineItemSchema
>;
export type ArRecentPayment = z.infer<typeof ArRecentPaymentSchema>;
export type GetArCustomerPaymentMethodMixInput = z.infer<
  typeof GetArCustomerPaymentMethodMixInputSchema
>;
export type ArCustomerPaymentMethodMix = z.infer<
  typeof ArCustomerPaymentMethodMixSchema
>;
export type ArCustomerInboundEmail = z.infer<
  typeof ArCustomerInboundEmailSchema
>;
export type ArCustomerOutboundEmail = z.infer<
  typeof ArCustomerOutboundEmailSchema
>;
